import jwtDecode from 'jwt-decode';

const API_URL = window.env ? window.env.API_SERVER : '';
const idToken = 'LIS_ID_TOKEN';
const refreshToken = 'LIS_REFRESH_TOKEN';
const claims = "LIS_CLAIMS";
const graphToken = 'GRAPH_ID_TOKEN';
const accessTokenExpiration = 'LIS_ACCESS_TOKEN_EXPIRATION';
const refreshTokenExpiration = 'LIS_REFRESH_TOKEN_EXPIRATION';

const NO_CONTENT = 2;
const REQUEST_FAILURE = 1;
const REQUEST_SUCCESS = 0;

let abortController = new window.AbortController();
let signal = abortController ? abortController.signal : "";

import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, usePKCE } from '../authConfig';
import { getMsalInstance } from './msal-instance.service';
import { authActionCreators } from '../stores/lis-auth-store';
import { msalConfig } from '../authConfig';
const inflightTokenRequests = new Map();

let reauthPromptPromise = null;

const clearMsalStorage = () => {
    Object.keys(localStorage)
        .filter(k => k.includes('login.windows.net'))
        .forEach(k => localStorage.removeItem(k));
};

const promptForReauthentication = (instance, account) => {
    if (reauthPromptPromise) {
        return reauthPromptPromise;
    }

    reauthPromptPromise = new Promise((resolve, reject) => {
        let d = document.getElementById('please-authenticate');
        if (d) {
            d.addEventListener('close', async (e) => {
                try {
                    if (e.target.returnValue === 'reauth') {
                        const loginResponse = await instance.loginPopup({
                            ...msalConfig,
                            loginHint: account?.username?.toLowerCase() || undefined
                        });
                        resolve(loginResponse);
                    } else {
                        resolve(null);
                    }
                } catch (err) {
                    reject(err);
                } finally {
                    reauthPromptPromise = null;
                }
            }, { once: true });
            return;
        }

        d = document.createElement('dialog');
        d.id = 'please-authenticate';
        const divHeader = document.createElement('div');
        divHeader.textContent = 'Session Expired';
        divHeader.className = 'dialog-header';
        const divBody = document.createElement('div');
        divBody.textContent = 'Please reauthenticate, or sign out.';
        divBody.className = 'dialog-body';
        const reauth = document.createElement('button');
        reauth.className = 'reathenticate';
        reauth.textContent = 'Reauthenticate';
        reauth.onclick = (e) => {
            e.preventDefault();
            d.close('reauth');
        };
        const signOut = document.createElement('button');
        signOut.className = 'sign-out';
        signOut.textContent = 'Sign Out';
        signOut.onclick = (e) => {
            e.preventDefault();
            d.close('');
        };
        const divButtons = document.createElement('div');
        divButtons.className = 'dialog-buttons';
        divButtons.append(reauth);
        divButtons.append(signOut);
        divBody.append(divButtons);
        d.appendChild(divHeader);
        d.appendChild(divBody);
        document.body.insertBefore(d, document.body.children[0]);
        d.showModal();

        d.addEventListener('close', async (e) => {
            try {
                if (e.target.returnValue === 'reauth') {
                    const loginResponse = await instance.loginPopup({
                        ...msalConfig,
                        loginHint: account?.username?.toLowerCase() || undefined
                    });
                    resolve(loginResponse);
                } else {
                    resolve(null);
                }
            } catch (err) {
                reject(err);
            } finally {
                d.remove();
                reauthPromptPromise = null;
            }
        }, { once: true });
    });

    return reauthPromptPromise;
};

const hasValidLisToken = () => {
    const token = localStorage.getItem(idToken);
    const tokenExp = localStorage.getItem(accessTokenExpiration);
    const hasClaims = !!localStorage.getItem(claims);
    const hasGraph = !!localStorage.getItem(graphToken);
    if (!token || token === 'undefined' || !hasClaims || !hasGraph) {
        return false;
    }

    if (!tokenExp || tokenExp === 'undefined') {
        return false;
    }

    return Number(tokenExp) > new Date().getTime();
};

export const getMsalRefreshToken = () => {
    try {
        const keys = Object.keys(localStorage);
        const refreshKey = keys.find(k => k.includes('-login.windows.net-refreshtoken-'));
        if (!refreshKey) return null;

        const raw = localStorage.getItem(refreshKey) || '';
        const match = raw.match(/["']secret["']\s*:\s*["']([^"']+)["']/i);
        return match?.[1] || null;
    } catch {
        return null;
    }
};

export const ensureLisTokenFromMsal = async () => {
    const instance = getMsalInstance();
    if (!instance) {
        throw [REQUEST_FAILURE, 'Error: MSAL instance not initialized'];
    }

    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (!account) {
        await window.store.dispatch(
            authActionCreators.logoutUser()
        )
        Object.keys(localStorage).filter(k => k.includes('login.windows.net')).forEach(k => localStorage.removeItem(k));
        throw [REQUEST_FAILURE, 'Error: No account in MSAL instance'];
    }

    const scopes = loginRequest.scopes || ['User.Read'];
    const key = account.homeAccountId + '|' + scopes.join(' ');

    if (inflightTokenRequests.has(key)) {
        await inflightTokenRequests.get(key);
        return localStorage.getItem(idToken);
    }

    const tokenPromise = (async () => {
        const request = { ...loginRequest, account };
        let msalResponse;

        try {
            msalResponse = await instance.acquireTokenSilent(request);
        } catch (err) {
            if (err instanceof InteractionRequiredAuthError ||
                ['interaction_required', 'login_required', 'consent_required'].includes(
                    err?.errorCode || err?.subError
                )) {
                msalResponse = await promptForReauthentication(instance, account);
                if (!msalResponse) {
                    await window.store.dispatch(authActionCreators.logoutUser());
                    clearMsalStorage();
                    throw [REQUEST_FAILURE, 'Reauthentication declined'];
                }
            } else {
                await window.store.dispatch(
                    authActionCreators.logoutUser()
                )
                clearMsalStorage();
                throw err;
            }
        }

        if (!hasValidLisToken()) {
            const msalRefreshToken = getMsalRefreshToken();
            if (!msalRefreshToken) {
                throw [REQUEST_FAILURE, 'Missing MSAL refresh token'];
            }
            await window.store.dispatch(
                authActionCreators.newLogin(msalResponse.accessToken, msalRefreshToken, msalResponse.expiresOn)
            );
        }
    })();

    inflightTokenRequests.set(key, tokenPromise);
    try {
        await tokenPromise;
        return localStorage.getItem(idToken);
    } finally {
        inflightTokenRequests.delete(key);
    }
};

export const getApiAccessToken = async () => {
    const instance = getMsalInstance();
    const account = instance?.getActiveAccount() || instance?.getAllAccounts()?.[0];
    const isHouse = account?.username?.toLowerCase().includes('@house.virginia.gov');

    if (!usePKCE || isHouse) {
        await checkToken();
        return localStorage.getItem(idToken);
    }

    return ensureLisTokenFromMsal();
};

export const cancelRequest = () => {
    if (abortController)
        abortController.abort();
}

//The only difference between publicRequest and partnerRequest is that partnerRequest checks to see if the token is valid and sends it with the fetch
export const publicRequest = (url, method, headers, body, params) => {
    let config = {
        method: method,
    }
    if (headers) {
        headers.WebAPIKey = process.env.REACT_APP_API_KEY;
        config.headers = headers;
    } else {
        config.headers = {
            WebAPIKey: process.env.REACT_APP_API_KEY
        }
    }
    if (body) {
        config.body = JSON.stringify(body)
    }
    if (signal && signal != "" && signal.aborted === true) {
        //Refresh the abortController because you can't reset the abort property of a signal
        abortController = new window.AbortController();
        signal = abortController ? abortController.signal : "";
    }
    if (signal && signal != "") {
        config.signal = signal;
    }
    return fetch(url + params, config)
        .then(response => {
            if (response.status === 204) {
                //Some endpoints do not return anything if there is no data to be returned. So NO_CONTENT must be specified as it is not always an error
                throw [NO_CONTENT, 'No Content'];
            } else if (response.status > 399) {
                //There was a request failure. Return the response. Do not try to convert it to a JSON object as there is no guarantee that it is formatted as a json
                return (response.text()
                    .then(text => {
                        console.log(text);
                        return Promise.reject([REQUEST_FAILURE, text])
                    })
                )
            }
            return response

        })
        .then(response =>
            //Our request is successful. Convert it to JSON
            response.json().then(data => ({ data, response }))
        )
        .then(({ data, response }) => {
            return [REQUEST_SUCCESS, data, response.headers]
        })
        .catch(err => {
            if (isAbortError(err)) {
                return [NO_CONTENT, 'Aborted'];
            }
            return err;
        });
}

export const partnerRequest = (url, method, headers, body, params, nonAbortable) => {
    return getApiAccessToken()
        .then((accessToken) => {
            //Just in case the method is initiated with a blank string as the headers
            if (!headers) {
                headers = {
                    'content-type': "application/json; charset=utf-8"
                }
            }
            headers.Authorization = "Bearer " + accessToken;
            headers.WebAPIKey = process.env.REACT_APP_API_KEY;
            let config = {
                method: method,
                headers: headers,
            }
            if (body) {
                if (body instanceof FormData) {
                    config.body = body
                } else {
                    config.body = JSON.stringify(body);
                }
            }
            if (signal && signal != "" && signal.aborted === true) {
                //Refresh the abortController because you can't reset the abort property of a signal
                abortController = new window.AbortController();
                signal = abortController ? abortController.signal : "";
            }
            if (signal && !nonAbortable) {
                config.signal = signal;
            }
            return fetch(url + params, config)
        })
        .then(response => {
            if (response.status === 204) {
                throw [NO_CONTENT, 'No Content'];
            } else if (response.status > 399) {
                return (response.text()
                    .then(text => {
                        return Promise.reject([REQUEST_FAILURE, text])
                    })
                )
            }
            return response

        })
        .then(response =>
            response.json().then(data => ({ data, response }))
        )
        .then(({ data, response }) => {
            return [REQUEST_SUCCESS, data, response.headers]
        })
        .catch(err => {
            if (isAbortError(err)) {
                return [NO_CONTENT, 'Aborted'];
            }
            return err;
        });
}

export const checkToken = () => {
    let token = localStorage.getItem(idToken);

    if (token !== null && token !== 'undefined') {
        const decode = jwtDecode(token);
        const stringToken = JSON.stringify(decode);
        const parsedToken = JSON.parse(stringToken);
        //If true then then expiration hasn't passed yet. So return a promise so partnerRequest can keep on running
        if (parsedToken.exp < new Date().getTime() / 1000) {
            return getNewToken(function (result) {
                return result
            })
        } else {
            return new Promise(function (resolve, reject) { resolve(true) });
        }
    } else {
        return Promise.reject([REQUEST_FAILURE, 'Error: No Token']);
    }
}

const getNewToken = (callback) => {
    let rtoken = localStorage.getItem(refreshToken);
    let config = {
        method: 'POST',
        headers: {
            "authorization": rtoken,
            "content-type": "application/json; charset=utf-8",
            "WebAPIKey": process.env.REACT_APP_API_KEY
        }
    }
    return fetch((API_URL || process.env.REACT_APP_AUTH_API_URL) + '/Authentication/api/RefreshAccessTokenAsync', config)
        .then(response => {
            if (response.status === 204) { return Promise.reject('Error: No Content') }
            else if (response.status === 401) {
                //The user's token is expired. Log them out
                localStorage.removeItem(idToken);
                localStorage.removeItem(refreshToken);
                localStorage.removeItem(refreshTokenExpiration);
                localStorage.removeItem(claims);
                localStorage.removeItem(graphToken);

                return Promise.reject("Error: Refresh Token Expired");
            }
            else if (response.status > 399) { return Promise.reject([REQUEST_FAILURE, response.text()]) }
            else { return response }
        })
        .then(response =>
            response.json().then(user => ({ user, response }))
        ).then(({ user, response }) => {
            // If refresh was successful, set the tokens in local storage
            let roles = [];
            if (user.Claims && user.Claims.Roles) {
                roles = user.Claims.Roles;
            }
            localStorage.setItem(claims, JSON.stringify(roles));
            localStorage.setItem(idToken, user.AccessToken);
            localStorage.setItem(refreshToken, user.RefreshToken);
            //604800000 is 7 days in milliseconds
            localStorage.setItem(refreshTokenExpiration, new Date().getTime() + 604800000)
            callback(0);
        }).catch(err => callback([REQUEST_FAILURE, Promise.reject(err)]))
}

const isAbortError = (err) =>
    err?.name === 'AbortError' ||
    err?.message?.includes('Aborted') ||
    err?.code === 20;
